import type { ReactElement } from 'react'
import { Link } from 'react-router'
import Hero from '../components/Hero'
import SectionHeader from '../components/SectionHeader'
import ServiceNotice from '../components/ServiceNotice'
import { useT } from '../i18n/useI18n'
import type { I18nContextValue } from '../i18n/context'
import { homeGroups, analysisSection } from '../data/homeCatalog'
import type { PricingApp } from '../data/pricing'
import { downloadPagePath } from '../data/downloads'
import './home.css'

function AppRowContent({ app, arrowIcon, t }: { app: PricingApp; arrowIcon: string; t: I18nContextValue['t'] }): ReactElement {
  return (
    <>
      <span className="hg-app-icon"><i className={`bi bi-${app.icon}`} /></span>
      <span className="hg-app-body">
        <span className="hg-app-top">
          <span className="hg-app-name">{t(`${app.i18nKey}.name`)}</span>
          <span className="hg-app-tag">{t(`${app.i18nKey}.tagline`)}</span>
        </span>
        <span className="hg-app-pitch">{t(`${app.i18nKey}.pitch`)}</span>
      </span>
      <span className="hg-app-arrow"><i className={`bi bi-${arrowIcon}`} /></span>
    </>
  )
}

// A web app opens in a new tab; a desktop app goes to its download page on this site.
function AppRow({ app, t }: { app: PricingApp; t: I18nContextValue['t'] }): ReactElement {
  if (app.launch === undefined) throw new Error(`Rendering the home catalogue failed: app ${app.id} is not live (no launch target) but was listed`)
  if (app.launch.kind === 'download') return <Link className="hg-app" to={downloadPagePath(app.id)}><AppRowContent app={app} arrowIcon="download" t={t} /></Link>
  return <a className="hg-app" href={app.launch.url} target="_blank" rel="noopener noreferrer"><AppRowContent app={app} arrowIcon="arrow-up-right" t={t} /></a>
}

export default function Home(): ReactElement {
  const t = useT()

  return (
    <>
      <Hero
        title={
          <>
            <span className="hero-accent">{t('home.hero.title')}</span>
          </>
        }
        subtitle={t('home.hero.subtitle')}
      />

      <section className="section section-soft">
        <div className="container">
          <SectionHeader
            title={t('home.products.heading')}
            subtitle={t('home.products.subheading')}
          />
          <div className="hg-catalog">
            {homeGroups.map((g) => (
              <div key={g.id} className="hg-sheet">
                <div className="hg-sheet-head">
                  <span className="hg-sheet-icon"><i className={`bi bi-${g.icon}`} /></span>
                  <div>
                    <h3 className="hg-sheet-name">{t(`${g.i18nKey}.name`)}</h3>
                    <span className="hg-sheet-pitch">{t(`${g.i18nKey}.pitch`)}</span>
                  </div>
                  <span className="hg-sheet-count">{t('pricing.group.toolsMany', { n: g.apps.length })}</span>
                </div>
                <div className="hg-apps">
                  {g.apps.map((a) => (
                    <AppRow key={a.id} app={a} t={t} />
                  ))}
                </div>
              </div>
            ))}
            {analysisSection.apps.length > 0 && (
              <div className="hg-sheet">
                <div className="hg-sheet-head">
                  <span className="hg-sheet-icon"><i className={`bi bi-${analysisSection.icon}`} /></span>
                  <div>
                    <h3 className="hg-sheet-name">{t(analysisSection.labelKey)}</h3>
                  </div>
                  <span className="hg-sheet-count">{t('pricing.group.toolsMany', { n: analysisSection.apps.length })}</span>
                </div>
                <div className="hg-apps">
                  {analysisSection.apps.map((a) => (
                    <AppRow key={a.id} app={a} t={t} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <ServiceNotice className="hg-service" />
        </div>
      </section>
    </>
  )
}
